import { handleError, handleResponse, MyAxios } from '@services/SettingAxios';
import { ServiceWidget } from '@services/ServiceWidget';
import { ServiceReviewsShop } from '@services/ServiceReviews';
import { ServiceInfoMessage } from '@services/ServiceInfoMessage';
import {
  TypePromiseAllShops,
  TypePromiseDataShops,
  TypePromiseFilters,
  TypePromiseInfoMapShop,
  TypePromiseShop,
  TypePromiseShops,
  TypePromiseShopsByProduct,
} from './TypePromise';
import { IShopMapListModel } from '@models/Header/IShopModel';
import { Code, Name, OneLevelService, PartnerShops, ServiceByShop, Shop } from '@app/Types/Shops';
import { PARTNER_STORE_WITHOUT_POSTS } from '@entities/Shop';

class ServiceShop {
  getShopIdByShopSlug(shops: IShopMapListModel[], slug: string): number {
    return shops.find(shop => shop.code.split('/')[1] === slug)?.id;
  }

  enrichmentDataWidgetShop(shops, dataWidget) {
    for (let i = 0; i < shops?.length; i++) {
      const data = dataWidget?.find(x => shops[i] && x?.shopId === shops[i]?.id);
      if (data) {
        shops[i] = {
          ...shops[i],
          record: data?.record,
          corporate: data?.corporate,
          oneLevelServices: [...shops[i].oneLevelServices.filter(x => x.code !== Code.Post), data.post],
        };
      }
    }
  }

  enrichmentPartnerShops(shops: Shop[]) {
    for (let i = 0; i < shops?.length; i++) {
      if (shops[i]?.partnerType === 3 && shops[i]?.id !== PARTNER_STORE_WITHOUT_POSTS) {
        this.addDefaultServices(shops[i]?.oneLevelServices);
      }
    }
  }

  addDefaultServices(oneLevelService: OneLevelService[]) {
    if (!oneLevelService.some(service => service.code === Code.Post)) {
      oneLevelService.push({
        code: Code.Post,
        name: Name['Количество постов'],
        post: 1,
      });
    }

    if (!oneLevelService.some(service => service.code === Code.Shinomontazh)) {
      oneLevelService.push({
        code: Code.NoWidgetShinomontazh,
        name: Name['Шиномонтаж'],
      });
    }
  }

  async initPageShops(regionId: number, slug: string) {
    const [shopsData, filter, comments, infoMessage] = await Promise.all([
      this.getDataShopsAndWidget(regionId),
      this.getFilters(regionId),
      ServiceReviewsShop.getReviewsRegion(regionId),
      ServiceInfoMessage.getInfoMessageShops(slug),
    ]);
    return { shopsData, filter, comments, infoMessage };
  }

  getMapShops(regionId: number): Promise<TypePromiseShops> {
    return MyAxios.get(`/api/shops/new-get-map-shops?region=${regionId}`)
      .then(handleResponse)
      .catch(error => handleError(error, []));
  }

  async getDataShops(regionId: number, listFilter: NonNullable<number[]> = []): Promise<TypePromiseDataShops> {
    return MyAxios.post(`/api/shops/get-shops-v1?region=${regionId}`, { listFilter })
      .then(handleResponse)
      .catch(error => handleError(error, { shops: [], visibleFilters: [] }));
  }

  async getShopsByProduct(productId: number, part: number, regionId: number): Promise<TypePromiseShopsByProduct[]> {
    return MyAxios.get(`/api/shops/get-shops-by-product?productId=${productId}&part=${part}&region=${regionId}`)
      .then(handleResponse)
      .catch(error => handleError(error, []));
  }

  async getDataShopsAndWidget(regionId: number, listFilter: NonNullable<number[]> = []): Promise<TypePromiseDataShops> {
    const { shops, visibleFilters } = await this.getDataShops(regionId, listFilter);
    this.enrichmentPartnerShops(shops);
    const dataWidget = !!shops.length && (await ServiceWidget.getRecordShm(shops.map(x => x.id)));
    dataWidget && this.enrichmentDataWidgetShop(shops, dataWidget);

    return { shops, visibleFilters, dataWidget };
  }

  async getFilters(regionId: number): Promise<TypePromiseFilters> {
    return MyAxios.get(`/api/shops/get-filter?region=${regionId}`)
      .then(handleResponse)
      .catch(error => handleError(error, []));
  }

  async getPartnerShops(regionId: number): Promise<PartnerShops[]> {
    return MyAxios.get(`/api/shops/get-partner-shops?region=${regionId}`)
      .then(handleResponse)
      .catch(error => handleError(error, []));
  }

  async getShop(slug: string | string[], regionId: number): Promise<TypePromiseShop & { redirect: string }> {
    return MyAxios.get(`/api/shops/v3/${slug}${regionId && `/?region=${regionId}`}`)
      .then(handleResponse)
      .catch(handleError);
  }

  async getShopAndWidget(slug: string | string[], regionId: number): Promise<TypePromiseShop & { redirect: string }> {
    const shop = await this.getShop(slug, regionId);

    if (!shop || shop.redirect) {
      return shop;
    }

    const { shopInfo } = shop;
    const dataWidget = await ServiceWidget.getDataShmByShop(shopInfo.id);

    if (dataWidget) {
      if (dataWidget.post?.post) {
        shopInfo.oneLevelServices.push(dataWidget.post);
      }
      shopInfo.loading = dataWidget.loading;
    }

    if (shopInfo.partners === 3 && shopInfo.id !== PARTNER_STORE_WITHOUT_POSTS) {
      this.addDefaultServices(shop.shopInfo.oneLevelServices);
    }

    return shop;
  }

  async getServicesByShop(slug: string): Promise<ServiceByShop[]> {
    return MyAxios.get(`/api/service/by-shop/${slug}`)
      .then(handleResponse)
      .catch(error => handleError(error, []));
  }

  async getMapInfoByShopId(shopId: number): Promise<TypePromiseInfoMapShop> {
    return MyAxios.get(`/api/shops/get-map-info?shopId=${shopId}`).then(handleResponse).catch(handleError);
  }

  async getAllShops(regionId: number): Promise<TypePromiseAllShops> {
    return MyAxios.get(`/api/shops/get-all-shops?regionId=${regionId}`)
      .then(handleResponse)
      .catch(error => handleError(error, []));
  }
}

const serviceShopInstance = new ServiceShop();

export default serviceShopInstance;
